import { mapGetters } from 'vuex'

import { shortPath } from 'app/releases'
import { marketing } from 'app/concerns'
import { determinations } from 'app/utils/accounts'
import merge from 'lodash/merge'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import snakeCase from 'lodash/snakeCase'

export const versionProvider = {
  provide() {
    return {
      version: this.version,
      urlPrefix: this.urlPrefix
    }
  },

  computed: {
    version() {
      return this.$route.meta.version
    },

    urlPrefix() {
      return `/${shortPath(this.$releases.active.version)}`
    }
  }
}

export const adFreeDocumentationPermitted = {
  computed: {
    ...mapGetters('permits', ['adFreeDocumentationVersions']),

    adFreeDocumentationPermitted() {
      return !!this.adFreeDocumentationVersions.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const hasAccessToProForActiveVersion = {
  computed: {
    ...mapGetters('permits', ['accessToProVersions']),

    hasAccessToProForActiveVersion() {
      return !!this.accessToProVersions.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const SubscriptionMixin = {
  computed: {
    ...mapGetters('accounts', ['currentAccount']),

    ...mapGetters('subscriptions', ['currentSubscription']),

    marketing() {
      return marketing({ account: this.currentAccount, subscription: this.currentSubscription })
    }
  }
}

export const EventTrackingMixin = {
  computed: {
    determinationsEventProps() {
      const dt = determinations({ account: this.$account })

      return {
        has_subscription: dt.hasSubscription,
        has_active_subscription: dt.hasActiveSubscription,
        has_canceled_subscription: dt.hasCanceledSubscription,
        has_free_subscription: dt.hasFreeSubscription,
        has_pro_subscription: dt.hasProSubscription,
        is_free: dt.isFree,
        is_backer: dt.isBacker,
        is_signed_in: this.$route.meta.isSignedIn
      }
    }
  },

  methods: {
    nameSlug(name) {
      return snakeCase(name)
    },

    purchaseFlowEvent(action, params = {}) {
      this.$posthog.capture(`purchase_flow:${action}`, merge(params, this.determinationsEventProps))
    },

    iconModalContext(isUsingModal) {
      return isUsingModal ? 'icon_modal' : 'icon_landing'
    },

    supportLinkClicked(context, location = null) {
      this.$posthog.capture('contact_support_link_click', {
        context: context,
        location: location
      })
    },

    fontawesomeDownloadEvent(type, medium, version, context = null, location = null) {
      this.$posthog.capture(
        `download:fontawesome_download_button_click`,
        merge({ type: type, medium: medium, version: version, context: context }, this.determinationsEventProps)
      )
    }
  }
}

export const CountdownMixin = {
  methods: {
    timeRemaining(endDate) {
      moment.updateLocale('en', {
        relativeTime: {
          d: '1 day',
          h: '1 hour',
          m: '1 minute',
          M: '1 month',
          y: '1 year'
        }
      })

      const humanReadable = moment.parseZone(endDate).fromNow(true)

      moment.updateLocale('en', null)

      return humanReadable
    },

    isInFuture(endDate) {
      return moment.utc().isBefore(moment.parseZone(endDate))
    }
  }
}

export const ABTestMixin = {
  computed: {
    getProPlansRoute() {
      return this.$posthog.getFeatureFlag('direct-to-pro-plan-2') === 'direct' ? { name: 'plans.pro' } : { name: 'plans' }
    }
  }
}

export const StickyElementMixin = {
  data() {
    return {
      isStickyElementStuck: false
    }
  },

  async updated() {
    this.$nextTick(this.setStickyElementParentRelative)
    this.$nextTick(this.setupStickyObserver)
  },

  methods: {
    setStickyElementParentRelative() {
      const parent = this.$refs.StickyElementParent

      if (parent) {
        parent.style.position = 'relative'
      }
    },

    setupStickyObserver() {
      const sentinel = this.$refs.StickyElementSentinel

      if (sentinel) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            this.isStickyElementStuck = !entry.isIntersecting
          },
          {
            root: null,
            threshold: 0
          }
        )

        observer.observe(sentinel)
      }
    }
  }
}
